<template>
  <div class="container-fluid py-3 position-relative">
    <div class="d-flex justify-content-center">
      <div class="card w-100">
        <!-- Header -->
        <div class="card-header bg-main text-white position-relative text-center">
          <strong class="h4 m-0">
            {{ view === 'list' ? 'DEVOLUCIONES DE ALTA' : 'DEVOLUCIONES - ' + patient_name }}
          </strong>
          <button type="button"
            class="btn btn-outline-main custom-btn position-absolute end-0 top-50 translate-middle-y me-3"
            @click="view === 'list' ? returnHome() : goBackToList()">
            <i class="fas fa-arrow-left" style="color:white"></i>
            {{ view === 'list' ? 'Volver' : 'Volver a la lista' }}
          </button>
        </div>

        <div class="card-body">
          <!-- ============================================================
               VISTA 1: LISTA DE PACIENTES DE ALTA
               ============================================================ -->
          <div v-if="view === 'list'">
            <!-- Filtros -->
            <div class="row g-2 align-items-end mb-3">
              <div class="col-xl-3 col-lg-3 col-md-6 col-sm-12">
                <label class="form-label mb-1 text-xs">Desde</label>
                <input type="date" class="form-control form-control-sm" v-model="filters.startDate" />
              </div>
              <div class="col-xl-3 col-lg-3 col-md-6 col-sm-12">
                <label class="form-label mb-1 text-xs">Hasta</label>
                <input type="date" class="form-control form-control-sm" v-model="filters.endDate" />
              </div>
              <div class="col-xl-3 col-lg-3 col-md-6 col-sm-12">
                <label class="form-label mb-1 text-xs">Paciente (nombre o DNI)</label>
                <input type="text" class="form-control form-control-sm" placeholder="Buscar paciente..."
                  v-model="filters.patient" @keyup.enter="applyFilters" />
              </div>
              <!-- <div class="col-xl-3 col-lg-3 col-md-6 col-sm-12">
                <label class="form-label mb-1 text-xs">Estado devolución</label>
                <select class="form-select form-select-sm" v-model="filters.devolution_status" @change="applyFilters">
                  <option value="all">Todos</option>
                  <option value="pending">Con pendientes por devolver</option>
                  <option value="complete">Ya devolvieron todo</option>
                </select>
              </div> -->
              <div class="col-xl-2 col-lg-2 col-md-12 col-sm-12 d-flex gap-2">
                <button class="btn btn-sm bg-main text-white w-100" @click="applyFilters">
                  <i class="fas fa-search"></i> Buscar
                </button>
                <button class="btn btn-sm btn-secondary w-100" @click="clearFilters">
                  <i class="fas fa-eraser"></i> Limpiar
                </button>
              </div>
            </div>

            <!-- Tabla -->
            <div class="table-responsive">
              <table :key="tableKey" class="table table-bordered table-sm align-middle text-center">
                <thead class="bg-main text-white">
                  <tr>
                    <th>N°</th>
                    <th>Paciente</th>
                    <th>Documento</th>
                    <th>Fecha Alta</th>
                    <th>Destino</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="7" class="text-center py-3">
                      <i class="fas fa-spinner fa-spin"></i> Cargando...
                    </td>
                  </tr>
                  <tr v-else-if="rows.length === 0">
                    <td colspan="7" class="text-center py-3">
                      <strong>No hay pacientes de alta con devoluciones</strong>
                    </td>
                  </tr>
                  <tr v-else v-for="(item, index) in rows" :key="'row-' + item.id + '-' + index">
                    <td>{{ (pagination.current_page - 1) * pagination.per_page + index + 1 }}</td>
                    <td class="text-start">{{ item.patient_name }}</td>
                    <td>{{ item.patient_document }}</td>
                    <td>{{ formatDateTime(item.date_discharge) }}</td>
                    <td>
                      <span class="badge bg-secondary">{{ item.destination }}</span>
                    </td>
                    <td>
                      <!--  Estado visual según devolution_status -->
                      <span v-if="item.devolution_status === 'complete'"
                            class="badge bg-success"
                            title="Ya devolvió todos los productos">
                        <i class="fas fa-check-circle"></i> COMPLETO
                      </span>
                      <span v-else
                            class="badge bg-warning text-dark"
                            :title="'Pendientes: ' + item.total_pending">
                        <i class="fas fa-hourglass-half"></i> PENDIENTE
                      </span>
                    </td>

                    <td>
                      <!-- Solo mostrar "Devolver" si aún hay pendientes -->
                      <button v-if="item.has_pending_returns"
                              class="btn btn-sm btn-success text-white me-1"
                              @click="openDevolver(item)"
                              title="Registrar devolución">
                        <i class="fas fa-undo"></i> Devolver
                      </button>
                      <!-- Historial: SIEMPRE visible -->
                      <button class="btn btn-sm btn-info text-white" @click="openHistory(item)"
                        title="Ver historial de PDFs">
                        <i class="fas fa-history"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Paginación -->
            <div class="d-flex justify-content-between align-items-center mt-2">
              <div class="text-xs text-muted">
                Mostrando {{ pagination.from || 0 }} - {{ pagination.to || 0 }} de
                {{ pagination.total }} registros
              </div>
              <nav>
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: pagination.current_page <= 1 }">
                    <a class="page-link" href="javascript:void(0)"
                      @click="goToPage(pagination.current_page - 1)">Anterior</a>
                  </li>
                  <li class="page-item" v-for="p in pagesToShow" :key="'page-' + p"
                    :class="{ active: p === pagination.current_page }">
                    <a class="page-link" href="javascript:void(0)" @click="goToPage(p)">{{ p }}</a>
                  </li>
                  <li class="page-item" :class="{ disabled: pagination.current_page >= pagination.last_page }">
                    <a class="page-link" href="javascript:void(0)"
                      @click="goToPage(pagination.current_page + 1)">Siguiente</a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          <!-- ============================================================
               VISTA 2: DETALLE DE DEVOLUCIONES DEL PACIENTE
               (igual que DevAsist.vue)
               ============================================================ -->
          <div v-else-if="view === 'detail'">
            <div class="row mx-0">
              <div class="mt-2 col-xl-2 col-lg-2 col-md-12">
                <select v-model="type" class="form-control form-control-sm" @change="mtdDevoluciones">
                  <option v-for="(item, index) in destino" :key="index" :value="item">
                    {{
                      item == 1 ? "CONSULTA"
                        : item == 3 ? "HOSPITALIZACIÓN"
                          : item == 6 ? "SOP"
                            : item == 4 ? "UCI ADULTO"
                              : "EMERGENCIA"
                    }}
                  </option>
                </select>
              </div>
              <div class="col-xl-6 col-lg-6 col-md-12 mt-2">
                <div class="input-group input-group-sm">
                  <div class="input-group-text">Paciente</div>
                  <input type="text" class="form-control form-control-sm" v-model="patient_name" readonly />
                </div>
              </div>
              <div class="col-xl-4 col-lg-4 col-md-12 mt-2 d-flex gap-2">
                <button class="btn btn-info btn-sm px-3" @click="mtdNewEv">
                  <i class="fas fa-plus"></i> Nueva devolución
                </button>
                <button v-if="devoluciones.length > 0" class="btn btn-success btn-sm px-3" @click="addReturns">
                  Devolver
                </button>
              </div>

              <div class="col-xl-4 col-lg-4 col-md-12 mt-2">
                <div class="input-group input-group-sm">
                  <span class="input-group-text"><i class="fas fa-search"></i></span>
                  <input type="text" class="form-control form-control-sm" placeholder="Buscar medicamento..."
                    v-model="searchMedicine" />
                </div>
              </div>

              <div class="row div-text mx-0 px-0 mb-2 text-center mt-3">
                <table class="table table-bordered">
                  <thead class="bg-main" style="font-size: 12px">
                    <tr class="text-white">
                      <th scope="col" class="align-middle" style="width: 10%">
                        <input class="form-check-input" type="checkbox" v-model="selectAll" title="Seleccionar todos" />
                      </th>
                      <th scope="col" class="align-middle" style="width: 70%">Nombre de Producto</th>
                      <th scope="col" class="align-middle" style="width: 10%">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody class="border-main" style="font-size: 11px" v-if="filteredDevoluciones.length > 0">
                    <tr v-for="(item, index) in filteredDevoluciones" :key="index">
                      <td class="align-middle">
                        <input class="form-check-input" v-model="item.status" type="checkbox" :id="'chk' + index" />
                      </td>
                      <td class="align-middle">
                        {{ item.molecule }} - {{ item.presentation }} - {{ item.dosis }}
                      </td>
                      <td class="text-center align-middle">{{ item.quantity }}</td>
                    </tr>
                  </tbody>
                  <tbody class="text-center" v-else>
                    <tr>
                      <td colspan="3" class="text-center"><strong>No hay resultados</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL DEVOLUCIÓN -->
    <cModalDevVue :title="modalDev.title" :boo_modal="modalDev.boo_modal" :item="modalDev.item" :patient_id="patient_id"
      :id_medical="id_medical" :selectedItems="modalDev.selectedItems" :typeAc="modalDev.typeAc"
      @updateDev="mtdDevolucionesUp" @mtdClose="mtdCloseModalDev" />

    <!-- MODAL NUEVA DEVOLUCIÓN -->
    <cModalDevNew :title="modalDevNew.title" :boo_modal="modalDevNew.boo_modal" :recipe="modalDevNew.recipe"
      @mtdClose="mtdClose" />

    <!-- MODAL HISTORIAL -->
    <div v-if="historyModal" class="modal-backdrop-custom" @click.self="closeHistory">
      <div class="modal-box">
        <div class="modal-header-custom">
          <strong>HISTORIAL DE {{ historyItem.patient_name }}</strong>
          <button class="btn btn-sm btn-light" @click="closeHistory">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body-custom">
          <div v-if="loadingHistory" class="text-center py-3">
            <i class="fas fa-spinner fa-spin"></i> Cargando...
          </div>
          <div v-else-if="historyNoteCredits.length === 0" class="text-center py-3">
            <strong>No hay Notas de Crédito para este paciente</strong>
          </div>
          <div v-else v-for="(nc, i) in historyNoteCredits" :key="'nc-' + nc.note_credit_id" class="nc-block mb-3">
            <div class="nc-header">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ nc.serie_number }}</strong>
                  <small class="ms-2 text-muted">{{ formatDateTime(nc.date) }}</small>
                </div>
                <button class="btn btn-sm btn-danger" @click="openPdfById(nc.note_credit_id)">
                  <i class="fas fa-file-pdf"></i> PDF
                </button>
              </div>
              <div class="mt-1 text-xs">
                <strong>Tipo:</strong> {{ nc.type_name }} &nbsp;|&nbsp;
                <strong>Motivo:</strong> {{ nc.motive_name }} &nbsp;|&nbsp;
                <strong>Usuario:</strong> {{ nc.user_name }}
              </div>
            </div>
            <table class="table table-bordered table-sm align-middle text-center mb-0">
              <thead class="bg-main text-white">
                <tr>
                  <th>N°</th>
                  <th>Producto</th>
                  <th>Comercial</th>
                  <th>Marca</th>
                  <th>Lote</th>
                  <th>F. Venc.</th>
                  <th>Cant.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="nc.detalles.length === 0">
                  <td colspan="7" class="text-center py-2">Sin productos</td>
                </tr>
                <tr v-else v-for="(d, j) in nc.detalles" :key="'det-' + i + '-' + j">
                  <td>{{ j + 1 }}</td>
                  <td class="text-start">{{ d.product_molecule }}</td>
                  <td class="text-start">{{ d.product_commercial }}</td>
                  <td>{{ d.product_brand }}</td>
                  <td>{{ d.lote }}</td>
                  <td>{{ formatDate(d.date_vcto) }}</td>
                  <td><strong>{{ d.devolution }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <SweetAlert :swal="swal" ref="SweetAlert" />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import SweetAlert from "@/components/shared/SweetAlert/SweetAlert.vue";
import cModalDevVue from "@/components/Farmacia/pages/Dev/Modals/cModalDev.vue";
import cModalDevNew from "@/components/Farmacia/pages/Dev/Modals/cModalDevNew.vue";
import moment from "moment";

export default {
  name: "DevolucionesAlta",
  components: { SweetAlert, cModalDevVue, cModalDevNew },

  props: {
    initial_patient_id: {
      type: Number,
      default: 0,
    },
    initial_medical_act_id: {
      type: Number,
      default: 0,
    },
    initial_patient_search: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      swal: null,
      view: "list",

      // Lista
      loading: false,
      rows: [],
      tableKey: 0,
      filters: {
        startDate: null,
        endDate: null,
        patient: "",
        devolution_status: "all", // 🆕
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        from: 0,
        to: 0,
      },

      // Detalle
      patient_id: 0,
      id_medical: 0,
      patient_name: "",
      destino: [],
      type: null,
      devoluciones: [],
      searchMedicine: "",

      // Modales
      modalDev: { title: "DEVOLUCIONES", item: {}, boo_modal: false, selectedItems: [], typeAc: null },
      modalDevNew: { title: "NUEVA DEVOLUCION", boo_modal: false, recipe: [] },

      // Historial
      historyModal: false,
      historyItem: {},
      historyNoteCredits: [],
      loadingHistory: false,
    };
  },
  computed: {
    pagesToShow() {
      const total = this.pagination.last_page;
      const current = this.pagination.current_page;
      const range = 2;
      const pages = [];
      const start = Math.max(1, current - range);
      const end = Math.min(total, current + range);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    filteredDevoluciones() {
      const query = this.searchMedicine.trim().toLowerCase();
      if (!query) return this.devoluciones;
      return this.devoluciones.filter((item) => {
        const text = `${item.molecule || ""} ${item.presentation || ""} ${item.dosis || ""}`.toLowerCase();
        return text.includes(query);
      });
    },
    selectAll: {
      get() {
        return (
          this.filteredDevoluciones.length > 0 &&
          this.filteredDevoluciones.every((item) => item.status)
        );
      },
      set(value) {
        this.filteredDevoluciones.forEach((item) => {
          item.status = value;
        });
      },
    },
  },
  created() {
    // 👇 Si vienen props iniciales → arranca en detalle
    if (this.initial_patient_id && this.initial_medical_act_id) {
      this.patient_id = this.initial_patient_id;
      this.id_medical = this.initial_medical_act_id;
      // 👇 Setear DNI/nombre en el filtro
      if (this.initial_patient_search) {
        this.filters.patient = this.initial_patient_search;
      }
      this.view = "detail";
      this.mtdselect();
    } else {
      this.mtdSetDefaultDates();
      this.mtdGetHistory(1);
    }
  },
  methods: {
    ...mapActions(["get", "post"]),

    returnHome() {
      this.$emit("returnHome");
    },

    mtdSetDefaultDates() {
      const today = moment();
      this.filters.endDate = today.format("YYYY-MM-DD");
      this.filters.startDate = today.clone().subtract(90, "days").format("YYYY-MM-DD");
    },

    /** ============================================================
     *  VISTA 1: Lista de pacientes de alta
     *  ============================================================ */
    mtdGetHistory(page = 1) {
      this.loading = true;
      const queryParts = [];
      queryParts.push("page=" + page);
      queryParts.push("per_page=" + this.pagination.per_page);
      if (this.filters.startDate) queryParts.push("startDate=" + encodeURIComponent(this.filters.startDate));
      if (this.filters.endDate) queryParts.push("endDate=" + encodeURIComponent(this.filters.endDate));
      if (this.filters.patient && this.filters.patient.trim() !== "") {
        queryParts.push("patient=" + encodeURIComponent(this.filters.patient.trim()));
      }
      // 🆕 Filtro de estado de devolución
      if (this.filters.devolution_status && this.filters.devolution_status !== "all") {
        queryParts.push("devolution_status=" + encodeURIComponent(this.filters.devolution_status));
      }

      const url = this.$store.getters.get__url + "/Pharmacy/returns/historyAltas?" + queryParts.join("&");
      console.log("🔍 URL historyAltas:", url);

      this.get({
        url: url,
        token: this.$store.getters.get__token,
      })
        .then((response) => {
          const data = response.data || {};
          this.rows = data.data || [];
          this.pagination.current_page = data.current_page || 1;
          this.pagination.last_page = data.last_page || 1;
          this.pagination.per_page = data.per_page || 15;
          this.pagination.total = data.total || 0;
          this.pagination.from = data.from || 0;
          this.pagination.to = data.to || 0;
          this.tableKey++;
        })
        .catch((error) => this.$refs.SweetAlert.showError(error))
        .finally(() => (this.loading = false));
    },

    applyFilters() {
      this.mtdGetHistory(1);
    },

    clearFilters() {
      this.filters.patient = "";
      this.filters.devolution_status = "all"; // 🆕
      this.mtdSetDefaultDates();
      this.mtdGetHistory(1);
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      if (page === this.pagination.current_page) return;
      this.mtdGetHistory(page);
    },

    /** ============================================================
     *  VISTA 2: Flujo de devolución (igual que DevAsist.vue)
     *  ============================================================ */
    openDevolver(item) {
      this.patient_id = item.patient_id;
      this.id_medical = item.medical_act_id;
      this.patient_name = item.patient_name;
      this.view = "detail";
      this.mtdselect();
    },

    goBackToList() {
      this.view = "list";
      this.devoluciones = [];
      this.destino = [];
      this.type = null;
      this.patient_name = "";
      this.patient_id = 0;
      this.id_medical = 0;
      this.searchMedicine = "";
      this.mtdSetDefaultDates();
      this.mtdGetHistory(1);
    },

    mtdselect() {
      this.post({
        url: this.$store.getters.get__url + "/Emergency/indexMovimientos",
        token: this.$store.getters.get__token,
        params: { id: this.id_medical },
      }).then((response) => {
        if (response.statusCode == 200) {
          let destinoold = response.data;
          this.destino = [];
          destinoold.forEach((item) => {
            if (item != 1) this.destino.push(item);
          });
          let num = this.destino.length;
          this.type = this.destino[num - 1];
          this.mtdDevoluciones();
        }
      });
    },

    mtdDevoluciones() {
      this.post({
        url: this.$store.getters.get__url + "/Pharmacy/returns/indexDevolucionesPharmacy",
        token: this.$store.getters.get__token,
        params: { id: this.id_medical, type: this.type },
      }).then((response) => {
        if (response.statusCode == 200) {
          this.devoluciones = response.data.devoluciones;
          this.patient_name = response.data.patient_name;
        }
      });
    },

    mtdDevolucionesUp(id_medical, type) {
      this.post({
        url: this.$store.getters.get__url + "/Pharmacy/returns/indexDevolucionesPharmacy",
        token: this.$store.getters.get__token,
        params: { id: id_medical, type: type },
      }).then((response) => {
        if (response.statusCode == 200) {
          this.devoluciones = response.data;
        }
      });
    },

    addReturns() {
      const selectedItems = this.devoluciones
        .filter((item) => item.status)
        .map((item) => ({
          id: item.id,
          recipe_detail_id: item.recipe_detail_id,
          total: item.quantity,
        }));

      if (selectedItems.length == 0) {
        this.$refs.SweetAlert.showWarning("No ha seleccionado ningún Producto para Devolver");
        return;
      }

      this.post({
        url: this.$store.getters.get__url + "/Pharmacy/returns/addProductDetail",
        token: this.$store.getters.get__token,
        params: { selectedItems, id_medical: this.id_medical },
      }).then((response) => {
        if (response.statusCode == 200) {
          if (response.data.length == 0) {
            this.modalDev = { title: "DEVOLUCIONES", item: {}, boo_modal: false, selectedItems: [] };
            this.$refs.SweetAlert.showWarning("Sin Datos");
          } else {
            this.modalDev.item = response.data;
            this.modalDev.selectedItems = selectedItems;
            this.modalDev.typeAc = this.type;
            this.modalDev.boo_modal = true;
          }
        }
      });
    },

    mtdCloseModalDev() {
      this.modalDev = { title: "DEVOLUCIONES", item: {}, boo_modal: false, selectedItems: [] };
    },

    mtdNewEv() {
      this.get({
        url: this.$store.getters.get__url + "/Pharmacy/returns/lastrecipe/" + this.id_medical,
        token: this.$store.getters.get__token,
      }).then((response) => {
        if (response.statusCode == 200) {
          this.modalDevNew.boo_modal = true;
          this.modalDevNew.title = "NUEVA DEVOLUCION - " + this.patient_name;
          this.modalDevNew.recipe = [];
          response.data.forEach((element) => this.modalDevNew.recipe.push(element));
        }
      });
    },

    mtdClose() {
      this.modalDevNew = { title: "NUEVA DEVOLUCION", boo_modal: false, recipe: [] };
    },

    /** ============================================================
     *  HISTORIAL DE PDFs
     *  ============================================================ */
    openHistory(item) {
      this.historyItem = item;
      this.historyModal = true;
      this.loadingHistory = true;
      this.historyNoteCredits = [];

      this.get({
        url: this.$store.getters.get__url + "/Pharmacy/returns/noteCreditsByPatient/" + item.patient_id,
        token: this.$store.getters.get__token,
      })
        .then((response) => {
          this.historyNoteCredits = response.data || [];
        })
        .catch((error) => this.$refs.SweetAlert.showError(error))
        .finally(() => (this.loadingHistory = false));
    },

    closeHistory() {
      this.historyModal = false;
      this.historyItem = {};
      this.historyNoteCredits = [];
    },

    openPdfById(noteCreditId) {
      if (!noteCreditId) {
        this.$refs.SweetAlert.showWarning("No se encontró la Nota de Crédito");
        return;
      }
      window.open(this.$store.getters.get__url + "/viewpdfNoteCreditInt/" + noteCreditId, "_blank");
    },

    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "-";
    },
    formatDateTime(date) {
      return date ? moment(date).format("DD/MM/YYYY HH:mm") : "-";
    },
  },
};
</script>

<style scoped>
.custom-btn {
  color: white;
  border-color: white;
}

.custom-btn:hover {
  background-color: #900052;
  color: white;
}

.text-xs {
  font-size: 12px;
}

.bg-main {
  background-color: #900052 !important;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1070;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-box {
  background: white;
  border-radius: 6px;
  width: 95%;
  max-width: 1100px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header-custom {
  background: #900052;
  color: white;
  padding: 12px 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
}

.modal-body-custom {
  padding: 16px;
}

.nc-block {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}

.nc-header {
  background: #f8f9fa;
  padding: 10px 14px;
  border-bottom: 1px solid #dee2e6;
}
</style>